import { useNegotiation } from '@context/negotiation.context';
import Button from '@components/atoms/button/button';
import React, { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useBucket } from '@context/bucket.context';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useUser from '@hooks/user/use-user';
import useFeatureFlag from '@hooks/use-feature-flags';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import MmaoOnboardingTooltip, {
  MmaoOnboardingTooltipRef,
} from '@components/mmao-onboarding-tooltip/mmao-onboarding-tooltip';
import { useInfoPopover } from '@hooks/info-popover/use-info-popover';
import { AppCookies } from '@enums/cookies';
import styles from '../CTA-product.module.scss';

export type OfferButtonProps = {
  onClick: () => void;
  className?: string;
  openFromAuthModal?: boolean;
  shouldDisplayAsLink?: boolean;
  setOpenNegotiationFromAuthModal: (value: boolean) => void;
};

const OfferButton: React.FC<OfferButtonProps> = ({
  onClick,
  openFromAuthModal,
  setOpenNegotiationFromAuthModal,
  className,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useUser();
  const { initialData, negotiation } = useNegotiation();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const isDisabled = !isAuthenticated ? false : !Boolean(initialData) || Boolean(negotiation);
  const { isSemPDP } = useFeatureFlag();
  const { isFeatureEnabled } = useBucket();
  const buttonSize = isHeroPdpStyles && !isSemPDP ? 'medium' : 'large';
  const tooltipRef = useRef<MmaoOnboardingTooltipRef>(null);

  const { hasSeenOnboardingTooltip, isTooltipEnabled } = useInfoPopover({
    cookieString: AppCookies.MMAO_ONBOARDING_TOOLTIP_SEEN,
    onlyForNewUsers: !!isAuthenticated,
  });
  const showToolTip = !isDisabled && isTooltipEnabled && isFeatureEnabled((f) => f.enableMmaoOnboardingTooltip, true);

  useEffect(() => {
    setOpenNegotiationFromAuthModal(false);
  }, [setOpenNegotiationFromAuthModal]);

  useEffect(() => {
    if (initialData && openFromAuthModal) {
      onClick();
    }
  }, [initialData, openFromAuthModal, onClick]);

  const handleClick = useCallback(() => {
    if (!hasSeenOnboardingTooltip) {
      tooltipRef.current?.hideTooltip();
    }
    onClick?.();
  }, [onClick, hasSeenOnboardingTooltip]);

  const mmaoButton = (
    <AuthenticationGuard
      className={clsx(styles.CTAButtonsBlock__authGuardWrapper, styles['CTAButtonsBlock__authGuardWrapper--MMAO'])}
      onAuth={() => {
        setOpenNegotiationFromAuthModal(true);

        // TODO: Add this only when the onboarding tooltip Feature Flag is enabled
        if (showToolTip) {
          // This is to prevent the tooltip from showing up when the user is authenticated via mmao button click
          tooltipRef.current?.hideTooltip();
        }
      }}
    >
      <Button
        disabled={isDisabled}
        onClick={showToolTip ? handleClick : onClick}
        variant="secondary"
        size={buttonSize}
        fullWidth={true}
        data-cy="pdp_mmao_btn"
        data-vc-dd-action-name="pdp_mmao_button"
        className={clsx(
          styles.CTAButtonsBlock__button,
          styles['CTAButtonsBlock__button--MMAO'],
          isHeroPdpStyles ? 'vc-btn--large--md-up' : '',
          className,
        )}
      >
        {t('PRODUCT.DETAILS.CTA.NEGOCIATE')}
      </Button>
    </AuthenticationGuard>
  );

  return showToolTip ? (
    <div className={styles.CTAButtonsBlock__buttonWrapper}>
      <MmaoOnboardingTooltip ref={tooltipRef}>{mmaoButton}</MmaoOnboardingTooltip>
    </div>
  ) : (
    <>{mmaoButton}</>
  );
};

export default OfferButton;
